import { Fragment } from "react";
import {
  STAT_GROUPS,
  deriveStats,
  formatAvg,
  formatPct,
  formatStatValue,
  type StatLine,
} from "@mpl/shared";

/**
 * Renders one or more stat lines side by side, grouped exactly as the league's
 * workbook groups them. Groups where nobody recorded anything are hidden, so a
 * game with only a partial stat line doesn't render as a wall of dashes.
 */
export default function StatLineTable({
  columns,
  showDerived = true,
}: {
  columns: { key: string; label: string; line: StatLine }[];
  showDerived?: boolean;
}) {
  if (columns.length === 0) return null;

  const anyValue = (key: string) => columns.some((c) => c.line[key] != null);

  const groups = STAT_GROUPS.map((g) => ({
    ...g,
    stats: g.stats.filter((s) => anyValue(s.key)),
  })).filter((g) => g.stats.length > 0);

  if (groups.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-sm text-chalk-500">
        No stats were recorded for this game.
      </p>
    );
  }

  const derived = columns.map((c) => deriveStats(c.line));

  // The floor scales with how many lines are being compared. A flat 420px was
  // set when this only ever showed one, and it pushed the second column off a
  // phone screen — which defeats the point of putting two side by side.
  const minWidth = 150 + 105 * columns.length;

  return (
    <div className="scroll-x">
      <table className="w-full" style={{ minWidth }}>
        <thead>
          <tr className="border-b border-field-700">
            <th className="th">Stat</th>
            {columns.map((c) => (
              <th key={c.key} className="th text-right">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* A bare <> as the array element carries no key, so React warned on
              every render — the keys below are on its children, not on it. */}
          {groups.map((g) => (
            <Fragment key={g.key}>
              <tr className="bg-field-850/60">
                <td
                  className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-chalk-400"
                  colSpan={columns.length + 1}
                >
                  {g.label}
                </td>
              </tr>
              {g.stats.map((s) => (
                <tr
                  key={`${g.key}-${s.key}`}
                  className="border-b border-field-800/60 last:border-b-0"
                >
                  <td className="td text-chalk-400" title={s.full}>
                    {s.label}
                  </td>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className="td text-right font-mono tabular-nums"
                    >
                      {formatStatValue(s.key, c.line[s.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}

          {showDerived && (
            <>
              <tr className="bg-field-850/60">
                <td
                  className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-chalk-400"
                  colSpan={columns.length + 1}
                >
                  Derived
                </td>
              </tr>
              <DerivedRow
                label="Completion %"
                values={derived.map((d) => formatPct(d.cmpPct))}
              />
              <DerivedRow
                label="Yards / attempt"
                values={derived.map((d) => formatAvg(d.passYpa))}
              />
              <DerivedRow
                label="Rushing yards"
                values={derived.map((d) => String(d.rushYds))}
              />
              <DerivedRow
                label="Yards / carry"
                values={derived.map((d) => formatAvg(d.rushAvg))}
              />
              <DerivedRow
                label="Receiving yards"
                values={derived.map((d) => String(d.recYds))}
              />
              <DerivedRow
                label="Total yards"
                values={derived.map((d) => String(d.totalYds))}
              />
              <DerivedRow
                label="3rd down"
                values={derived.map((d) => formatPct(d.thirdDownPct, 0))}
              />
              <DerivedRow
                label="Giveaways"
                values={derived.map((d) => String(d.turnoversCommitted))}
              />
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DerivedRow({ label, values }: { label: string; values: string[] }) {
  return (
    <tr className="border-b border-field-800/60 last:border-b-0">
      <td className="td text-chalk-400">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="td text-right font-mono tabular-nums text-chalk-200">
          {v}
        </td>
      ))}
    </tr>
  );
}
